// Authentication context: login/logout, token validation on load (kept through
// a server outage), and idle-timeout auto-logout. Provides `useAuth()` to the whole app.
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import { useAppContext } from '../context/AppContext';
import useIdleTimeout from '../hooks/useIdleTimeout';
import { clearAll as clearCoPilotCache } from '../services/copilotLocalStore';

const IDLE_TIMEOUT_MS = 12 * 60 * 60 * 1000;
const VALIDATE_RETRY_MS = 5000;

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Epoch ms since the stored session could not be checked (server unreachable).
  const [serverUnreachableSince, setServerUnreachableSince] = useState(null);
  const navigate = useNavigate();
  const { setCurrentRole } = useAppContext();

  // Validate a stored token on load so a refresh keeps the session. An
  // unreachable server keeps the token and the check repeats until it answers;
  // only a refusal ends the session.
  // See docs/design-decisions.md#ui-connection-lost-overlay
  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;

    const checkLoggedIn = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setServerUnreachableSince(null);
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }
      try {
        const userData = await authService.validateToken();
        if (cancelled) return;
        setUser(userData);
        setCurrentRole(userData.role);
        setIsAuthenticated(true);
        setServerUnreachableSince(null);
      } catch (error) {
        if (cancelled) return;
        if (error.unreachable) {
          setServerUnreachableSince((since) => since ?? Date.now());
          retryTimer = setTimeout(checkLoggedIn, VALIDATE_RETRY_MS);
        } else {
          localStorage.removeItem('token');
          setIsAuthenticated(false);
          setUser(null);
          setServerUnreachableSince(null);
          setError("Your session has ended. Please sign in again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    checkLoggedIn();
    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
  }, [setCurrentRole]);

  const login = async (username, password) => {
    setLoading(true);
    setError('');
    
    try {
      const data = await authService.login(username, password);
      
      if (!data || !data.token) {
        throw new Error("Invalid authentication response");
      }
      
      setUser(data.user);
      localStorage.setItem('token', data.token);
      setCurrentRole(data.user.role);
      setIsAuthenticated(true);
      
      navigate('/');
      
      return { success: true, user: data.user };
    } catch (error) {
      const errorMessage = error.message || "Authentication failed";
      setError(errorMessage);
      setIsAuthenticated(false);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = useCallback((reason = 'manual') => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('token');
    localStorage.removeItem('lastActivityTimestamp');
    // Drop per-session UI drafts (usePersistedState) so half-filled forms and
    // filters don't carry over to the next user on a shared panel.
    try { sessionStorage.clear(); } catch { /* storage unavailable */ }
    // Same for the browser-side CoPilot conversation cache.
    clearCoPilotCache();
    navigate('/login');
  }, [navigate]);

  // Auto-logout after IDLE_TIMEOUT_MS (12h) of inactivity.
  useIdleTimeout(
    () => logout('idle'),
    IDLE_TIMEOUT_MS,
    isAuthenticated
  );

  const value = { 
    user, 
    login, 
    logout, 
    loading,
    error,
    isAuthenticated,
    serverUnreachableSince,
    clearError: () => setError(null)
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
